/* eslint-disable no-shadow */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Placeholder,
  Toast,
  Link,
  IconChevronRight16,
  IconSuccess16,
  IconError16,
  Text,
} from '@stitch-fix/mode-react';
import { useSelectEvent } from '@stitch-fix/event-reporter';
import { useI18n } from '@stitch-fix/i18n';
import ClickTooltip from '../shared/ClickTooltip';
import { sendCount } from '../../utils/sendMetrics';
import { incrementBagCount } from '../ShoppingBag/helpers/shoppingBagCount';
import {
  IShoppingBag,
  IShoppingBagItem,
} from '../ShoppingBag/ShoppingBag.interfaces';
import {
  trackAddToBagSuccess,
  trackAddToBagToastClick,
  trackBagCreatedEvent,
} from './AddToBagButton.tracking';
import formatBusinessLine from '../../helpers/businessLineFormatter';
import onError from '../../utils/onError';
import { useShoppingBagAddItemMutation } from './shoppingBagAddItem';
import { useProductDetailsPageContext } from '../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';
import {
  allowClick,
  unselectedStateMessage,
} from '../../helpers/unselectedStateHelper';
import ShoppingBagTracker from './shoppingBagTracker';
import waysToWearItQuickViewTracker from '../WaysToWearItCarousel/waysToWearItQuickViewTracker';

const AddToBagButton = ({
  relatedItemSkuIds,
  isSkuCardFormat,
  label,
}: {
  relatedItemSkuIds: (number | string)[];
  isSkuCardFormat?: boolean;
  label?: string;
}) => {
  const BAG_QUANTITY_LIMIT = 100;
  const LOCAL_STORAGE_KEY = 'ShoppingBag';
  const [itemAdded, setItemAdded] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [errorRetryCount, setErrorRetryCount] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [toastToDisplay, setToastToDisplay] = useState('');
  const [shoppingBagBusinessLine, setShoppingBagBusinessLine] = useState('');

  const { reportSelectEvent } = useSelectEvent();
  const { locale } = useI18n();

  enum ErrorType {
    GENERAL_ERROR = 'sku_card_add_to_bag_error',
    LIMIT_ERROR = 'sku_card_bag_limit_error',
    BUSINESS_LINE_ERROR = 'sku_card_business_line_error',
  }

  const [state, dispatch] = useProductDetailsPageContext();

  const businessLine = state.productBusinessLine || '';
  const skuId = state.selectedSkuId || '';
  const source = state.source || 'ungated_shop';
  const subSource = state.subSource || '';
  const collection = state.collectionName || '';
  const externalProductId = state.externalProductId;
  const skuAvailability = state.selectedVariantAvailability?.toString() || '';

  const addToBagError = (errorKey: string): void => {
    setHasError(true);
    setToastToDisplay(errorKey);
    sendCount('add_to_bag_error', { error: errorKey });
    if (isSkuCardFormat) {
      setErrorRetryCount(prevRetryCount => prevRetryCount + 1);
      sendCount('quick_view_add_to_bag.error', {
        source,
        collection,
        retry: errorRetryCount,
      });
    }
  };

  const addToBagSuccess = (): void => {
    setErrorRetryCount(0);
    incrementBagCount();
    setHasError(false);
    setToastToDisplay('success');
    setIsDisabled(false);
    trackAddToBagSuccess({ skuId, skuAvailability, subSource });
    if (isSkuCardFormat) {
      const { trackQuickViewAddToBag } = waysToWearItQuickViewTracker;

      if (skuId) {
        trackQuickViewAddToBag({
          skuId,
          collection,
          outfitId: state.outfitId,
          source,
          pdpSkuId: state.productSkuId,
          externalProductId,
        });
      }
    }
    reportSelectEvent({
      schema: 'sku_select',
      name: 'add_to_bag',
      action_name: 'add_to_bag',
      sku_id: `${skuId}`,
      // @ts-ignore we need to pass in this value but cannot change the type
      // in reportSelectEvent
      external_product_id: `${externalProductId}`,
    });
  };

  const getTotalBagQuantity = (
    shoppingBagItems: Map<string, IShoppingBagItem>,
  ): number => {
    let totalQuantity = 0;

    shoppingBagItems.forEach(value => {
      totalQuantity += value.quantity;
    });

    return totalQuantity;
  };

  const [shoppingBagAddItemMutation] = useShoppingBagAddItemMutation({
    variables: {
      input: {
        sku: {
          id: skuId?.toString() || state?.selectedSkuId?.toString() || '',
        },
        shoppingBagContext: {
          source,
          subsource: subSource,
          collection,
        },
      },
    },
    onCompleted: data => {
      const result = data?.shoppingBagAddItem?.result;
      // eslint-disable-next-line no-underscore-dangle
      const typename = result?.__typename;

      switch (typename) {
        case 'ShoppingBagAddItemError':
          if (result?.code === 'BAG_FULL') {
            addToBagError(ErrorType.LIMIT_ERROR);
          }
          break;
        case 'ShoppingBagAddItemSuccess':
          if (!isSkuCardFormat) {
            ShoppingBagTracker.trackAddToBagEvent({
              skuId,
              canonicalName: 'pdp',
              source,
              collectionName: collection,
              keptSkuId: state.selectedSkuId,
              outfitId: state.outfitId,
              relatedItemSkuIds,
              subtotal: result?.shoppingBag.subtotal,
              subSource,
              externalProductId,
              locale,
            });
            if (state.shoppingBagEmpty && !itemAdded) {
              ShoppingBagTracker.trackBagCreatedEvent(
                result?.shoppingBag.id,
                result?.shoppingBag.items,
                result?.shoppingBag.outOfStocks,
              );
            }
          }
          setItemAdded(true);
          addToBagSuccess();
          break;
        default:
          throw new Error(
            `Encountered unsupported ShoppingBagAddItemResult type: ${typename}`,
          );
      }

      setIsDisabled(false);
    },
    onError: () => {
      addToBagError(ErrorType.GENERAL_ERROR);
      setIsDisabled(false);
    },
  });

  // The add to bag method fired when the user is currently logged in.
  const gatedAddToBag = async () => {
    try {
      sendCount('add_to_bag_clicked', {
        gated: state.gated,
        selectedSkuIdSameAsOnPageLoad:
          state.selectedSkuId === state.recommendedSkuId,
        selectedColorIsSameAsOnPageLoad:
          state.selectedUniqueColorId === state.selectedUniqueColorIdOnPageLoad,
        selectedSizeIsSameAsOnPageLoad:
          state.selectedSizeLabel === state.selectedSizeLabelOnPageLoad,
        selectedInseamIsSameAsOnPageLoad:
          state.selectedInseamLabel === state.selectedInseamLabelOnPageLoad,
      });
      setIsDisabled(true);
      shoppingBagAddItemMutation();
    } catch {
      addToBagError('There was an error adding this item to your bag.');
      setIsDisabled(false);
    }
  };

  const addToUngatedBag = () => {
    try {
      setIsDisabled(true);

      const selectedItem: string = skuId.toString();
      const selectedItemDetails: IShoppingBagItem = {
        quantity: 1,
        businessLine,
        similar_items: relatedItemSkuIds,
        source,
        sub_source: subSource,
        collection,
        related_item_sku_ids: relatedItemSkuIds,
        addedAt: Date.now(),
      };

      const shoppingBag = localStorage.getItem(LOCAL_STORAGE_KEY);

      if (!shoppingBag) {
        const newShoppingBagItems = new Map<string, IShoppingBagItem>();

        newShoppingBagItems.set(selectedItem, selectedItemDetails);

        window.localStorage.setItem(
          LOCAL_STORAGE_KEY,
          JSON.stringify({
            items: Object.fromEntries(newShoppingBagItems),
            createdOn: Date.now(),
            totalQuantity: 1,
            businessLine,
          }),
        );
        trackBagCreatedEvent({ skuId, bagItem: selectedItemDetails });
        addToBagSuccess();
      } else {
        const currentShoppingBag: IShoppingBag = JSON.parse(shoppingBag);
        const isEmptyBag = !currentShoppingBag.businessLine;
        const isBusinessLineMatch =
          businessLine === currentShoppingBag.businessLine;

        if (isEmptyBag || isBusinessLineMatch) {
          const currentShoppingBagItems = new Map(
            Object.entries(currentShoppingBag.items),
          );

          const totalBagQuantity: number = getTotalBagQuantity(
            currentShoppingBagItems,
          );

          if (totalBagQuantity < BAG_QUANTITY_LIMIT) {
            if (currentShoppingBagItems.has(selectedItem)) {
              const currentItem = currentShoppingBagItems.get(
                selectedItem,
              ) as IShoppingBagItem;

              selectedItemDetails.quantity = currentItem.quantity + 1;
            }

            currentShoppingBagItems.set(selectedItem, selectedItemDetails);

            window.localStorage.setItem(
              LOCAL_STORAGE_KEY,
              JSON.stringify({
                items: Object.fromEntries(currentShoppingBagItems),
                createdOn: currentShoppingBag.createdOn,
                totalQuantity: totalBagQuantity + 1,
                businessLine,
              }),
            );
            addToBagSuccess();
          } else {
            addToBagError(ErrorType.LIMIT_ERROR);
            setIsDisabled(false);
          }
        } else {
          setShoppingBagBusinessLine(currentShoppingBag.businessLine);
          addToBagError(ErrorType.BUSINESS_LINE_ERROR);
          setIsDisabled(false);
        }
      }
    } catch (error) {
      onError(error);
      addToBagError(ErrorType.GENERAL_ERROR);
      setIsDisabled(false);
    }
  };

  const isLoading = isDisabled && !hasError;
  const buttonText = isLoading ? 'Adding to bag...' : label;

  if (state.loading || !state.shopProduct) {
    return <Placeholder height={4} />;
  }

  return (
    <>
      {allowClick(state) ? (
        <Button
          width="fill"
          onClick={state.gated ? gatedAddToBag : addToUngatedBag}
          type="submit"
          name="commit"
          variant={isSkuCardFormat ? 'slim' : 'filled-statement'}
          data-testid="add-to-bag-button"
        >
          {buttonText}
        </Button>
      ) : (
        <ClickTooltip
          text={unselectedStateMessage(state)}
          show={
            !allowClick(state) && state.ctaTooltipDisplay?.showAddToBag === true
          }
          arrowDirection="down"
        >
          <Button
            width="fill"
            aria-label="warning"
            onClick={() => {
              dispatch({ type: 'DISABLED_ADD_TO_BAG_CLICKED' });
            }}
            type="submit"
            name="commit"
            variant="filled-statement"
            data-testid="add-to-bag-button-blocker"
          >
            {buttonText}
          </Button>
        </ClickTooltip>
      )}
      <Toast
        data-testid="success-toast"
        isOpen={toastToDisplay === 'success'}
        onClose={() => setToastToDisplay('')}
        startContent={<IconSuccess16 purpose="decorative" />}
        endContent={
          <Link
            endIcon={<IconChevronRight16 purpose="decorative" />}
            href={
              state.gated ? '/my-items/shopping_bag' : '/product/shopping_bag'
            }
            size="tertiary"
            variant="inherit"
            onClick={trackAddToBagToastClick}
          >
            Go to bag
          </Link>
        }
      >
        Added to your bag
      </Toast>
      <Toast
        data-testid="lob-mismatch-toast"
        isOpen={toastToDisplay === ErrorType.BUSINESS_LINE_ERROR}
      >
        <Text>
          Our apologies! Men’s and Women’s styles need to be purchased through
          separate accounts
          <br />
          Please buy the {formatBusinessLine(shoppingBagBusinessLine)} styles in
          your bag first and then create a new account with a different email
          address to buy {formatBusinessLine(businessLine)} styles. Sorry for
          the hassle.
        </Text>
      </Toast>
      <Toast
        isOpen={toastToDisplay === ErrorType.GENERAL_ERROR}
        onClose={() => setToastToDisplay('')}
      >
        The item wasn&apos;t added to your bag. Please try again.
      </Toast>
      <Toast
        isOpen={toastToDisplay === ErrorType.LIMIT_ERROR}
        onClose={() => setToastToDisplay('')}
        startContent={<IconError16 purpose="decorative" />}
        endContent={
          <Link
            endIcon={<IconChevronRight16 purpose="decorative" />}
            href={
              state.gated ? '/my-items/shopping_bag' : '/product/shopping_bag'
            }
            size="tertiary"
            variant="inherit"
          >
            Go to bag
          </Link>
        }
      >
        Your bag is full. Remove items to add more.
      </Toast>
    </>
  );
};

AddToBagButton.propTypes = {
  relatedItemSkuIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ),
  isSkuCardFormat: PropTypes.bool,
  label: PropTypes.string,
};

AddToBagButton.defaultProps = {
  relatedItemSkuIds: [],
  isSkuCardFormat: false,
  label: 'Add to bag',
};

export default AddToBagButton;
